import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Plus, Upload, Download } from "lucide-react";
import { api, API } from "@/lib/quotationApi";
import FinishRowHeader from "@/components/admin/finishes/FinishRowHeader";
import FinishPriceCell from "@/components/admin/finishes/FinishPriceCell";
import FinishesTotalsRow from "@/components/admin/finishes/FinishesTotalsRow";

/**
 * Tabular editor for finishes.
 * Rows    = finishes (Acrylic, Laminate, Glass Shutter …)
 * Columns = pricing tiers (Standard / Premium / Premium Plus …)
 * Cells   = ₹/sqft for that finish at that tier, inline-editable.
 */
export default function FinishesTable() {
  const [items, setItems] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);

  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState("");
  const [newPrice, setNewPrice] = useState("");

  // Cell being edited: `${finishId}:${tierId}` → draft string
  const [editingCell, setEditingCell] = useState(null);
  const [cellDraft, setCellDraft] = useState("");

  // Finish row header rename mode
  const [editingName, setEditingName] = useState(null);
  const [nameDraft, setNameDraft] = useState("");

  const fileInputRef = useRef(null);

  const orderedTiers = useMemo(
    () => tiers.slice().sort((a, b) => (a.order || 0) - (b.order || 0)),
    [tiers],
  );

  // Column totals (sum + avg of effective ₹/sqft across all finishes for each tier).
  const columnStats = useMemo(() => {
    const effective = (finish, tier) => {
      if (tier.is_base) return Number(finish.price_per_sqft) || 0;
      const o = finish.tier_prices?.[tier.id];
      return o != null && o !== ""
        ? Number(o) || 0
        : Number(finish.price_per_sqft) || 0;
    };
    const stats = {};
    for (const t of orderedTiers) {
      let sum = 0;
      for (const f of items) sum += effective(f, t);
      const count = items.length;
      stats[t.id] = { sum, avg: count ? sum / count : 0, count };
    }
    return stats;
  }, [items, orderedTiers]);

  const load = async () => {
    setLoading(true);
    try {
      const [f, t] = await Promise.all([
        api.get("/finishes"),
        api.get("/settings/tiers-v2"),
      ]);
      setItems(f.data || []);
      setTiers(t.data?.tiers || []);
    } catch {
      toast.error("Failed to load finishes");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const onAdd = async () => {
    if (!newName.trim() || !newPrice) return;
    try {
      await api.post("/finishes", {
        name: newName.trim(),
        price_per_sqft: parseFloat(newPrice),
      });
      setNewName("");
      setNewPrice("");
      setAdding(false);
      toast.success("Finish added");
      load();
    } catch {
      toast.error("Add failed");
    }
  };

  const onDeleteFinish = async (id, name) => {
    if (!window.confirm(`Delete finish "${name}"?`)) return;
    try {
      await api.delete(`/finishes/${id}`);
      toast.success("Deleted");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const startCellEdit = (finish, tier) => {
    const key = `${finish.id}:${tier.id}`;
    const current = tier.is_base
      ? finish.price_per_sqft
      : finish.tier_prices?.[tier.id] ?? "";
    setEditingCell(key);
    setCellDraft(current === "" || current == null ? "" : String(current));
  };

  const cancelCellEdit = () => {
    setEditingCell(null);
    setCellDraft("");
  };

  const saveCell = async (finish, tier) => {
    const num = parseFloat(cellDraft);
    if (!Number.isFinite(num) || num < 0) {
      toast.error("Enter a valid price");
      return;
    }
    try {
      if (tier.is_base) {
        await api.put(`/finishes/${finish.id}`, { price_per_sqft: num });
      } else {
        const merged = { ...(finish.tier_prices || {}), [tier.id]: num };
        await api.put(`/finishes/${finish.id}`, { tier_prices: merged });
      }
      toast.success("Price updated");
      cancelCellEdit();
      load();
    } catch {
      toast.error("Update failed");
    }
  };

  const clearTierOverride = async (finish, tier) => {
    if (tier.is_base) return;
    try {
      const merged = { ...(finish.tier_prices || {}) };
      delete merged[tier.id];
      await api.put(`/finishes/${finish.id}`, { tier_prices: merged });
      toast.success("Cleared override");
      cancelCellEdit();
      load();
    } catch {
      toast.error("Update failed");
    }
  };

  const startNameEdit = (finish) => {
    setEditingName(finish.id);
    setNameDraft(finish.name);
  };

  const saveNameEdit = async (finish) => {
    const nm = nameDraft.trim();
    if (!nm) {
      toast.error("Name cannot be empty");
      return;
    }
    try {
      await api.put(`/finishes/${finish.id}`, { name: nm });
      toast.success("Renamed");
      setEditingName(null);
      load();
    } catch {
      toast.error("Rename failed");
    }
  };

  const onExportCSV = () => {
    window.open(`${API}/admin/export/finishes`, "_blank");
  };

  const onImportCSV = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const form = new FormData();
    form.append("file", file);
    try {
      const r = await api.post("/admin/import/finishes", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Imported: ${r.data.added} added, ${r.data.updated} updated`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import failed");
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  return (
    <div>
      {/* CSV actions */}
      <div className="grid grid-cols-2 gap-2 mb-3">
        <button
          onClick={onExportCSV}
          className="btn-ghost !h-11 text-sm flex items-center justify-center gap-2"
          data-testid="export-finish-btn"
        >
          <Download size={14} />
          Export CSV
        </button>
        <button
          onClick={() => fileInputRef.current?.click()}
          className="btn-ghost !h-11 text-sm flex items-center justify-center gap-2"
          data-testid="import-finish-btn"
        >
          <Upload size={14} />
          Import CSV
        </button>
        <input
          type="file"
          ref={fileInputRef}
          accept=".csv,text/csv"
          className="hidden"
          onChange={onImportCSV}
        />
      </div>

      {!adding && (
        <button
          onClick={() => setAdding(true)}
          className="btn-ghost w-full flex items-center justify-center gap-2 mb-4"
          data-testid="add-finish-btn"
        >
          <Plus size={16} />
          Add Finish
        </button>
      )}

      {adding && (
        <div className="card-luxe mb-4">
          <div className="overline text-[#C5A880] mb-3">New Finish</div>
          <label className="label-luxe">Name</label>
          <input
            className="input-luxe"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="e.g. Premium Acrylic"
            data-testid="new-finish-name"
          />
          <label className="label-luxe mt-4">Base price / sqft (₹)</label>
          <input
            type="number"
            className="input-luxe"
            value={newPrice}
            onChange={(e) => setNewPrice(e.target.value)}
            inputMode="numeric"
            data-testid="new-finish-price"
          />
          <div className="flex gap-2 mt-4">
            <button className="btn-ghost flex-1" onClick={() => setAdding(false)}>
              Cancel
            </button>
            <button
              onClick={onAdd}
              disabled={!newName.trim() || !newPrice}
              className="btn-primary flex-1"
              data-testid="save-finish-btn"
            >
              Save
            </button>
          </div>
        </div>
      )}

      {loading && (
        <div className="card-luxe p-4 text-white/40 text-sm">Loading...</div>
      )}

      {!loading && items.length === 0 && (
        <div className="card-luxe p-6 text-center text-white/40 text-sm">
          No finishes yet. Tap &quot;Add Finish&quot; above.
        </div>
      )}

      {!loading && items.length > 0 && orderedTiers.length > 0 && (
        <div
          className="card-luxe !p-0 overflow-auto max-h-[70vh]"
          data-testid="finishes-table-wrap"
        >
          <table
            className="min-w-full border-collapse text-sm"
            data-testid="finishes-table"
          >
            <thead>
              <tr className="border-b border-white/10">
                <th className="sticky top-0 left-0 z-30 bg-[#111]/95 backdrop-blur px-3 py-3 text-left text-[11px] uppercase tracking-wider text-white/50 font-medium min-w-[160px]">
                  Finish ↓ / Tier →
                </th>
                {orderedTiers.map((tier) => (
                  <th
                    key={tier.id}
                    className="sticky top-0 z-20 bg-[#111]/95 backdrop-blur px-3 py-3 text-left align-top min-w-[140px] border-l border-white/5"
                    data-testid={`tier-col-${tier.id}`}
                  >
                    <div
                      className="font-semibold text-sm leading-tight"
                      style={{ color: tier.color || "#C5A880" }}
                    >
                      {tier.name}
                    </div>
                    <div className="text-[10px] text-white/40 mt-0.5 font-normal">
                      {tier.is_base ? "Base price" : "₹/sqft"}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map((f) => (
                <tr
                  key={f.id}
                  className="border-b border-white/5 last:border-0"
                  data-testid={`finish-row-${f.id}`}
                >
                  <FinishRowHeader
                    finish={f}
                    isEditing={editingName === f.id}
                    nameDraft={nameDraft}
                    onDraftChange={setNameDraft}
                    onStartEdit={() => startNameEdit(f)}
                    onSave={() => saveNameEdit(f)}
                    onCancel={() => setEditingName(null)}
                    onDelete={() => onDeleteFinish(f.id, f.name)}
                  />
                  {orderedTiers.map((tier) => (
                    <FinishPriceCell
                      key={tier.id}
                      finish={f}
                      tier={tier}
                      isEditing={editingCell === `${f.id}:${tier.id}`}
                      draft={cellDraft}
                      onDraftChange={setCellDraft}
                      onStartEdit={() => startCellEdit(f, tier)}
                      onSave={() => saveCell(f, tier)}
                      onCancel={cancelCellEdit}
                      onClearOverride={() => clearTierOverride(f, tier)}
                    />
                  ))}
                </tr>
              ))}
            </tbody>
            <FinishesTotalsRow
              orderedTiers={orderedTiers}
              columnStats={columnStats}
              finishCount={items.length}
            />
          </table>
        </div>
      )}

      {!loading && items.length > 0 && orderedTiers.length === 0 && (
        <div className="card-luxe p-6 text-center text-white/40 text-sm">
          Add pricing tiers first (Tiers tab) to enable the tabular view.
        </div>
      )}

      <div className="text-[10px] text-white/30 mt-3 text-center">
        Tap any cell to edit its price. Non-base tiers fall back to the base price
        until you set an override.
      </div>
    </div>
  );
}
